import { osolutionClient } from "./axios";

// Type Definitions
export interface TemplateCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  parent: {
    id: number;
    name: string;
  } | false;
  template_count: number;
  sequence: number;
}

export interface TemplateTag {
  id: number;
  name: string;
  color: number;
}

export interface TemplateData {
  export_type?: string;
  export_date?: string;
  odoo_version?: string;
  odashboard_version?: string;
  configs: any[];
  config?: any; // For single page templates
}

export interface OdashTemplate {
  id: number;
  name: string;
  technical_name: string;
  short_description: string;
  description: string;
  category: {
    id: number;
    name: string;
    description: string;
    icon: string;
  } | false;
  tags: TemplateTag[];
  template_type: "page" | "dashboard_set" | "component_library";
  component_type?: "data" | "graph" | "table" | false;
  preview_image: string | false;
  template_data: TemplateData;
  page_count: number;
  component_count: number;
  config_count: number;
  version: string;
  odoo_version: string;
  required_models: string;
  required_modules: string;
  author_type: "official" | "partner" | "community";
  sequence: number;
}

export interface OdashTemplateDetails extends OdashTemplate {
  screenshots: {
    id: number;
    name: string;
    description: string;
    image: string | false;
    sequence: number;
  }[];
  compatibility_version: string;
  author_partner_id: string | false;
  publish_date: string | false;
  notes: string;
}

// API Functions
export const fetchPublishedTemplates = async (): Promise<OdashTemplate[]> => {
  try {
    const response = await osolutionClient.get("/templates");
    return response.data.result || [];
  } catch (error) {
    console.error("Error fetching templates:", error);
    return [];
  }
};

export const fetchTemplateDetails = async (
  templateId: number
): Promise<OdashTemplateDetails | null> => {
  try {
    const response = await osolutionClient.get(`/templates/${templateId}`);
    return response.data.result || null;
  } catch (error) {
    console.error("Error fetching template details:", error);
    return null;
  }
};

export const fetchTemplateCategories = async (): Promise<
  TemplateCategory[]
> => {
  try {
    const response = await osolutionClient.get("/template-categories");
    return response.data.result || [];
  } catch (error) {
    console.error("Error fetching template categories:", error);
    return [];
  }
};

// Helper Functions
export const getTemplatesByType = (
  templates: OdashTemplate[],
  type: "page" | "dashboard_set" | "component_library"
): OdashTemplate[] => {
  return templates.filter((template) => template.template_type === type);
};

export const getTemplatesByCategory = (
  templates: OdashTemplate[],
  categoryId: number
): OdashTemplate[] => {
  return templates;
};

export const groupTemplatesByCategory = (
  templates: OdashTemplate[],
  categories: TemplateCategory[]
) => {
  return categories.map((category) => ({
    ...category,
    templates: getTemplatesByCategory(templates, category.id),
  }));
};

// Get block type from template config
export const getBlockTypeFromConfig = (
  config: any
): "data" | "graph" | "table" | null => {
  if (!config) return null;
  
  const type = config.type;
  if (type === "block") return "data";
  if (type === "graph") return "graph";
  if (type === "table") return "table";
  
  return null;
};

// Count blocks by type in template
export const countBlocksByType = (template: OdashTemplate) => {
  const counts = {
    data: 0,
    graph: 0,
    table: 0,
  };
  
  if (!template.template_data || !template.template_data.configs) {
    return counts;
  }
  
  template.template_data.configs.forEach((config: any) => {
    const blockType = getBlockTypeFromConfig(config.config);
    if (blockType) {
      counts[blockType]++;
    }
  });
  
  return counts;
};

// Filter templates for block type
export const getTemplatesForBlockType = (
  templates: OdashTemplate[],
  blockType: "data" | "graph" | "table"
): OdashTemplate[] => {
  return templates.filter((template) => {
    // Component library templates
    if (template.template_type === "component_library") {
      return template.template_data.configs.some((config: any) => {
        return getBlockTypeFromConfig(config.config) === blockType;
      });
    }
    return false;
  });
};

// Get individual block configs from template
export const getBlockConfigsFromTemplate = (
  template: OdashTemplate,
  blockType?: "data" | "graph" | "table"
): any[] => {
  if (!template.template_data || !template.template_data.configs) {
    return [];
  }
  
  return template.template_data.configs.filter((config: any) => {
    if (!blockType) return true;
    return getBlockTypeFromConfig(config.config) === blockType;
  });
};
